import React from 'react'
import classNames from 'classnames'
import { PauseIcon } from '@/atoms/Icons/PauseIcon'
import { PlayIcon } from '@/atoms/Icons/PlayIcon'
import { useHlsPlayerUI } from './HlsPlayerUIContext'
import { HlsSkipButton } from './HlsSkipButton'

/** Play button circle dimensions. The pause icon is 80% of the circle size. */
const PLAY_BUTTON_SIZE_PX = 72
const PLAY_ICON_SIZE_PX = 44
const PAUSE_ICON_SIZE_PX = PLAY_BUTTON_SIZE_PX * 0.8
const MOBILE_PLAY_BUTTON_SIZE_PX = 48
const MOBILE_PLAY_ICON_SIZE_PX = 28
const MOBILE_PAUSE_ICON_SIZE_PX = MOBILE_PLAY_BUTTON_SIZE_PX * 0.8

interface HlsCenterPlayOverlayProps {
  visible: boolean
}

/**
 * Center overlay with skip backward, play/pause, and skip forward buttons.
 * Clicking the background area (outside buttons) also toggles playback.
 *
 * Play state: white circle with dark play icon.
 * Pause state: bare white pause icon (no background), 80% of the play circle height.
 * Transitions between states with a cross-fade animation.
 */
export const HlsCenterPlayOverlay: React.FC<HlsCenterPlayOverlayProps> = ({ visible }) => {
  const { playing, controller } = useHlsPlayerUI()

  const handleBackgroundClick = () => {
    if (!controller) return
    if (playing) {
      controller.pause()
    } else {
      controller.play()
    }
  }

  const handlePlayPauseClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!controller) return
    if (playing) {
      controller.pause()
    } else {
      controller.play()
    }
  }

  return (
    <div
      className="absolute inset-0 flex cursor-pointer items-center justify-center"
      onClick={handleBackgroundClick}
      role="presentation"
    >
      {/* Center button cluster: skip back | play/pause | skip forward */}
      <div
        className={classNames(
          'flex items-center gap-6 transition-opacity duration-[400ms] sm:gap-12',
          visible ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
      >
        <HlsSkipButton direction="backward" />

        {/* Play/pause button — both rendered, cross-faded via opacity */}
        <button
          type="button"
          className="relative flex h-12 w-12 items-center justify-center sm:h-[4.5rem] sm:w-[4.5rem]"
          onClick={handlePlayPauseClick}
          aria-label={playing ? 'Pause' : 'Play'}
        >
          {/* Play: white circle with dark icon */}
          <div
            className={classNames(
              'absolute inset-0 flex items-center justify-center rounded-full bg-white transition-opacity duration-[400ms]',
              playing ? 'opacity-0' : 'opacity-100 hover:bg-white/90',
            )}
          >
            <PlayIcon size={MOBILE_PLAY_ICON_SIZE_PX} color="core-gray-950" className="sm:hidden" />
            <PlayIcon size={PLAY_ICON_SIZE_PX} color="core-gray-950" className="hidden sm:block" />
          </div>

          {/* Pause: bare white icon, no background */}
          <div
            className={classNames(
              'absolute inset-0 flex items-center justify-center transition-opacity duration-[400ms]',
              playing ? 'opacity-100' : 'opacity-0',
            )}
          >
            <PauseIcon size={MOBILE_PAUSE_ICON_SIZE_PX} color="core-gray-100" className="sm:hidden" />
            <PauseIcon size={PAUSE_ICON_SIZE_PX} color="core-gray-100" className="hidden sm:block" />
          </div>
        </button>

        <HlsSkipButton direction="forward" />
      </div>
    </div>
  )
}
